import React, { useState } from 'react';
import { CARD } from '../ui';
import { DATA_HUE, TRACK } from '../dashboard/theme';
import { formatDay } from '../dashboard/range';
import StatTile from '../dashboard/StatTile';
import LineItems from './LineItems';
import MonthlyChart from './MonthlyChart';
import { InvoiceActionButtons, InvoiceDetail, InvoiceStatus, dueLine } from './InvoiceParts';
import {
  addMonths,
  formatDollars,
  formatMoney,
  formatMonthLong,
  formatStorage,
  monthAxisLabel,
  monthsBetween,
  plural,
} from './format';

const WARNING = '#fab219';
const CRITICAL = '#d03b3b';

const Notice = ({ tone, title, children, action }) => {
  const styles = {
    info: 'border-sky-500/30 bg-sky-500/10 text-sky-200',
    warning: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
    critical: 'border-red-500/40 bg-red-500/10 text-red-200',
  }[tone];
  return (
    <div className={`rounded-xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3 ${styles}`}>
      <div className="min-w-0">
        <p className="font-semibold">{title}</p>
        {children && <p className="text-sm opacity-90 mt-0.5">{children}</p>}
      </div>
      {action}
    </div>
  );
};

// A usage figure against what the plan includes. The fill turns amber from 80%
// and red past the allowance, and the caption says so in words too, so the
// state never rests on color.
const UsageMeter = ({ label, used, included, format = (n) => Number(n || 0).toLocaleString(), caption, note }) => {
  const ratio = included ? used / included : null;
  const over = ratio !== null && ratio > 1;
  const near = ratio !== null && !over && ratio >= 0.8;
  const color = over ? CRITICAL : near ? WARNING : DATA_HUE;
  const state =
    ratio === null ? null : over ? `Over by ${format(used - included)}` : `${Math.round(ratio * 100)}% used`;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-gray-300">{label}</span>
        <span className="text-sm text-white tabular-nums">
          {format(used)}
          {included != null && <span className="text-gray-500"> of {format(included)}</span>}
        </span>
      </div>
      {ratio !== null && (
        <div className="mt-2 h-1.5 w-full rounded-full overflow-hidden" style={{ backgroundColor: TRACK }}>
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, ratio * 100)}%`, backgroundColor: color }} />
        </div>
      )}
      <p className="mt-1.5 text-xs text-gray-500">
        {state && <span className={over ? 'text-red-300' : near ? 'text-amber-300' : ''}>{state}</span>}
        {state && caption ? ' · ' : ''}
        {caption}
      </p>
      {note && <p className="mt-0.5 text-xs text-gray-500">{note}</p>}
    </div>
  );
};

const planTerms = (plan) =>
  [
    `${formatMoney(plan.seat_cents)} per seat`,
    plan.platform_cents ? `${formatDollars(plan.platform_cents)} platform fee` : null,
    `${plan.submissions_per_seat.toLocaleString()} submissions and ${plan.storage_gb_per_seat} GB per seat`,
    `${plan.guest_submissions_included.toLocaleString()} guest submissions, then ${formatMoney(plan.guest_submission_cents)} each`,
  ].filter(Boolean);

const PlanComparison = ({ data, canChange, onChangePlan }) => {
  const current = data.subscription?.plan;
  return (
    <div className={`${CARD} p-6`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Compare plans</h2>
          <p className="text-sm text-gray-500 mt-1">List price for a full month at your current pace.</p>
        </div>
        {canChange && (
          <button
            type="button"
            onClick={onChangePlan}
            className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border border-purple-400/40 px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0"
          >
            {current ? 'Change plan' : 'Set plan'}
          </button>
        )}
      </div>
      <ul className="space-y-3">
        {data.comparison.map((row) => {
          const plan = data.plans.find((p) => p.key === row.plan);
          const isCurrent = row.plan === current;
          return (
            <li
              key={row.plan}
              className={`rounded-lg border px-4 py-3 ${isCurrent ? 'border-purple-400/50 bg-purple-500/10' : 'border-gray-700 bg-gray-900/30'}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-white">{row.name}</span>
                  {isCurrent && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-200 border border-purple-400/40">Current</span>
                  )}
                </span>
                <span className={`text-sm font-semibold tabular-nums ${row.fits ? 'text-white' : 'text-gray-500'}`}>
                  {formatMoney(row.total_cents)}
                  <span className="text-xs font-normal text-gray-500">/mo</span>
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{plan.tagline}</p>
              {!row.fits && (
                <p className="text-xs text-amber-300 mt-1">
                  Your {data.usage.seats} seats are over its {plan.max_seats}-seat limit.
                </p>
              )}
              <ul className="mt-2 space-y-0.5 text-xs text-gray-400">
                {planTerms(plan).map((term) => (
                  <li key={term}>{term}</li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
      {!canChange && (
        <p className="mt-4 text-xs text-gray-500">To change plans, contact us and we'll switch you from the next bill.</p>
      )}
    </div>
  );
};

const InvoiceHistory = ({ invoices, expanded, onToggle, actions }) => {
  const live = new Set(invoices.filter((i) => i.status !== 'void').map((i) => i.period));
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="px-6 py-5 border-b border-gray-700">
        <h2 className="text-lg font-semibold text-white">Invoices</h2>
        <p className="text-sm text-gray-500 mt-1">One per month, issued after the month ends.</p>
      </div>
      {invoices.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-gray-500">No invoices yet.</p>
      ) : (
        <ul className="divide-y divide-gray-700/60">
          {invoices.map((invoice) => {
            const open = expanded === invoice.id;
            return (
              <li key={invoice.id} id={`invoice-${invoice.id}`}>
                <div className="flex flex-wrap items-center gap-3 px-6 py-3">
                  <button
                    type="button"
                    onClick={() => onToggle(open ? null : invoice.id)}
                    aria-expanded={open}
                    className="flex-1 min-w-[12rem] flex items-center justify-between gap-3 bg-transparent hover:bg-transparent p-0 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-white group-hover:text-purple-300 transition-colors">
                        {formatMonthLong(invoice.period)}
                      </span>
                      <span className="block text-xs text-gray-500">{invoice.number}</span>
                    </span>
                    <span className="flex items-center gap-3 flex-shrink-0">
                      <InvoiceStatus invoice={invoice} />
                      <span className="text-right">
                        <span className={`block text-sm font-semibold tabular-nums ${invoice.status === 'void' ? 'text-gray-500 line-through' : 'text-white'}`}>
                          {formatMoney(invoice.total_cents)}
                        </span>
                        <span className="block text-xs text-gray-500">{dueLine(invoice)}</span>
                      </span>
                    </span>
                  </button>
                  {actions && (
                    <InvoiceActionButtons invoice={invoice} actions={actions} liveSibling={live.has(invoice.period)} />
                  )}
                </div>
                {open && (
                  <div className="px-6 pb-5 pt-1 bg-gray-900/30">
                    <InvoiceDetail invoice={invoice} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// A company's bill: what this month is costing so far, what's included and how
// much of it is used, how the plans compare, and every invoice. Admins see
// their own company; devs see any, with the plan and invoice actions.
const AccountView = ({ data, actions, onChangePlan }) => {
  const [expanded, setExpanded] = useState(null);
  const { usage, period, subscription, plan, estimate, projected_estimate: projected } = data;
  const state = subscription?.state || 'none';
  const month = period.month;
  const allowances = data.allowances;
  const seatsByRole = usage.seats_by_role;
  const balance = data.balance;
  const dev = Boolean(actions);

  // Billed by month: the last year of invoices, then this month's running
  // estimate, drawn lighter.
  const invoicesByMonth = new Map();
  for (const invoice of data.invoices) {
    if (invoice.status !== 'void') invoicesByMonth.set(invoice.period, invoice);
  }
  const chartMonths = monthsBetween(addMonths(month, -11), month);
  const columns = chartMonths.map((key, index) => {
    const isCurrent = key === month;
    const total = isCurrent ? (state === 'active' && estimate ? estimate.total_cents : 0) : invoicesByMonth.get(key)?.total_cents || 0;
    return {
      key,
      axisLabel: monthAxisLabel(key, index),
      fullLabel: formatMonthLong(key),
      segments: { billed: total },
      total,
      estimate: isCurrent,
    };
  });
  const hasHistory = data.invoices.length > 0;

  const openInvoiceFor = (key) => {
    const invoice = invoicesByMonth.get(key);
    if (!invoice) return;
    setExpanded(invoice.id);
    window.setTimeout(() => {
      document.getElementById(`invoice-${invoice.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  const heroValue = state === 'active' && estimate ? formatMoney(estimate.total_cents) : state === 'trial' ? '$0.00' : '—';
  const heroCaption = {
    active:
      projected && projected.total_cents !== estimate?.total_cents
        ? `On pace for ${formatMoney(projected.total_cents)} by ${formatDay(period.end)}`
        : `Day ${period.days_elapsed} of ${period.days_total}`,
    trial: estimate ? `On ${plan.name} it would be ${formatMoney(estimate.total_cents)} so far` : null,
    ended: 'Billing has ended',
    none: 'Not on a plan yet',
  }[state];

  return (
    <div className="space-y-6">
      {state === 'none' && (
        <Notice
          tone="info"
          title="Not billed yet"
          action={
            dev && (
              <button type="button" onClick={onChangePlan} className="bg-sky-500/20 hover:bg-sky-500/30 text-sky-100 border border-sky-400/40 px-3 py-1.5 rounded-lg text-sm font-medium">
                Set plan
              </button>
            )
          }
        >
          {data.company.name} isn't on a plan, so nothing is invoiced. The plans below show what this month's usage would cost.
        </Notice>
      )}
      {state === 'trial' && (
        <Notice tone="info" title={`Free trial — billing starts ${formatMonthLong(subscription.starts_on)}`}>
          Nothing is invoiced until then. The estimate shows what this month would cost on {plan.name}.
        </Notice>
      )}
      {state === 'ended' && (
        <Notice tone="info" title={`Billing ended after ${formatMonthLong(subscription.ends_on)}`} />
      )}
      {balance.overdue_cents > 0 && (
        <Notice tone="critical" title={`${formatMoney(balance.overdue_cents)} is overdue`}>
          Open the invoices below to see which months are still unpaid.
        </Notice>
      )}
      {plan?.max_seats && usage.seats > plan.max_seats && (
        <Notice tone="warning" title={`${usage.seats} seats is over the ${plan.name} plan's ${plan.max_seats}-seat limit`}>
          Nothing is blocked, but a bigger plan fits a team this size — see the comparison below.
        </Notice>
      )}

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile hero label={`${formatMonthLong(month)} so far`} value={heroValue} caption={heroCaption} />
        <StatTile
          label="Balance due"
          value={formatMoney(balance.outstanding_cents)}
          caption={
            balance.outstanding_cents === 0
              ? 'All paid up'
              : balance.overdue_cents > 0
                ? `${formatMoney(balance.overdue_cents)} overdue`
                : `Next due ${formatDay(balance.next_due_on, { month: 'short', day: 'numeric', year: 'numeric' })}`
          }
        />
        <StatTile
          label="Plan"
          value={plan ? plan.name : 'None'}
          comparisonLabel={
            plan
              ? `${formatMoney(subscription.seat_cents ?? plan.seat_cents)} per seat${
                  subscription.discount_percent ? ` · ${subscription.discount_percent}% off` : ''
                }`
              : 'Not billed'
          }
          meter={plan?.max_seats ? { value: usage.seats, total: plan.max_seats } : null}
          caption={plan ? (plan.max_seats ? `${usage.seats} of ${plan.max_seats} seats` : 'No seat limit') : null}
        />
        <StatTile
          label="Billable seats"
          value={usage.seats.toLocaleString()}
          comparisonLabel={[
            seatsByRole.admin && plural(seatsByRole.admin, 'admin'),
            seatsByRole.manager && plural(seatsByRole.manager, 'manager'),
            seatsByRole.inspector && plural(seatsByRole.inspector, 'inspector'),
          ].filter(Boolean).join(' · ')}
          caption={usage.pending_seats ? `${plural(usage.pending_seats, 'invite')} not accepted yet — not billed` : null}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className={`${CARD} p-6 lg:col-span-2`}>
          <div className="mb-3">
            <h2 className="text-lg font-semibold text-white">
              {state === 'active' ? 'This month’s bill so far' : 'This month at list price'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {formatDay(period.start)} – {formatDay(period.end, { month: 'short', day: 'numeric', year: 'numeric' })}.
              {state === 'active' && ' The invoice is issued once the month ends; usage lines only appear past your allowance.'}
            </p>
          </div>
          {estimate ? (
            <>
              <LineItems bill={estimate} totalLabel={state === 'active' ? 'Estimated total' : 'Would be'} />
              {state === 'active' && projected && projected.total_cents !== estimate.total_cents && (
                <p className="mt-3 text-sm text-gray-400">
                  At the pace so far, the month ends around{' '}
                  <span className="text-white font-medium">{formatMoney(projected.total_cents)}</span>.
                </p>
              )}
            </>
          ) : (
            <p className="py-6 text-sm text-gray-400">
              No plan, so there's no bill. Usage is still measured — it's on the right, and the comparison below prices it.
            </p>
          )}
        </div>

        <div className={`${CARD} p-6`}>
          <h2 className="text-lg font-semibold text-white mb-1">Usage this month</h2>
          <p className="text-sm text-gray-500 mb-5">
            {allowances ? 'Pooled across your whole team.' : 'Measured, not billed.'}
          </p>
          <div className="space-y-5">
            <UsageMeter
              label="Seats"
              used={usage.seats}
              included={allowances?.max_seats ?? null}
              caption={allowances && !allowances.max_seats ? 'No seat limit' : allowances ? 'Plan limit' : null}
            />
            <UsageMeter
              label="Submissions"
              used={usage.submissions}
              included={allowances?.submissions ?? null}
              caption={
                data.projected_usage.submissions !== usage.submissions
                  ? `on pace for ${data.projected_usage.submissions.toLocaleString()}`
                  : null
              }
              note={usage.resubmissions ? `${plural(usage.resubmissions, 're-submission')} — corrections are free` : null}
            />
            <UsageMeter
              label="Guest submissions"
              used={usage.guest_submissions}
              included={allowances?.guest_submissions ?? null}
              caption={
                data.projected_usage.guest_submissions !== usage.guest_submissions
                  ? `on pace for ${data.projected_usage.guest_submissions.toLocaleString()}`
                  : null
              }
            />
            <UsageMeter
              label="Photo storage"
              used={usage.storage_bytes}
              included={allowances?.storage_bytes ?? null}
              format={formatStorage}
              caption="all photos kept"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {hasHistory ? (
            <MonthlyChart
              title="Billed by month"
              subtitle="Click a month to open its invoice."
              columns={columns}
              series={[{ id: 'billed', label: 'Billed', color: DATA_HUE }]}
              onSelect={openInvoiceFor}
              selectLabel="Open the invoice for"
              footnote={`${formatMonthLong(month)} is drawn lighter: it's the running estimate, not an invoice yet.`}
            />
          ) : (
            <div className={`${CARD} p-6 h-full`}>
              <h2 className="text-lg font-semibold text-white">Billed by month</h2>
              <p className="py-12 text-center text-sm text-gray-500">Your first invoice will appear here after a full month on a plan.</p>
            </div>
          )}
        </div>
        <PlanComparison data={data} canChange={dev} onChangePlan={onChangePlan} />
      </div>

      <InvoiceHistory invoices={data.invoices} expanded={expanded} onToggle={setExpanded} actions={actions} />
    </div>
  );
};

export default AccountView;
